import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Paper,
  Grid,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { CertificateListAction, CreateCertificateAction } from "../../Redux/Actions/RewardAction";
import { useS3Upload } from "../../hooks/useS3Upload";
import { ApiConfig } from "../../Config/CommonConfig";

interface ConfigField {
  key: string;
  x: number;
  y: number;
  width: number;
  height: number;
  font_family: string;
  font_size: number;
  color: string;
  align: "left" | "center" | "right";
  valign: "top" | "middle" | "bottom";
  placeholder: string;
}

interface CreateCertificateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const DEFAULT_FIELD: ConfigField = {
  key: "",
  x: 0,
  y: 0,
  width: 300,
  height: 50,
  font_family: "Montserrat-Medium.ttf",
  font_size: 31,
  color: "#000000",
  align: "left",
  valign: "top",
  placeholder: "",
};

const CreateCertificateModal: React.FC<CreateCertificateModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch<any>();
  const {
    uploadFile,
    isLoading: s3Loading,
    s3Path,
    success: uploadSuccess,
    error: uploadError,
    resetState: resetS3State,
  } = useS3Upload();

  const [loading, setLoading] = useState(false);
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [configFields, setConfigFields] = useState<ConfigField[]>([
    {
      key: "issued_date",
      x: 336,
      y: 554,
      width: 300,
      height: 50,
      font_family: "Montserrat-Medium.ttf",
      font_size: 31,
      color: "#333333",
      align: "left",
      valign: "top",
      placeholder: "Nov 14, 2024",
    },
    {
      key: "name",
      x: 336,
      y: 617,
      width: 800,
      height: 76,
      font_family: "Montserrat-Medium.ttf",
      font_size: 62,
      color: "#000000",
      align: "left",
      valign: "top",
      placeholder: "Participant Name",
    },
    {
      key: "awarded_for",
      x: 336,
      y: 720,
      width: 500,
      height: 50,
      font_family: "Montserrat-Medium.ttf",
      font_size: 31,
      color: "#666666",
      align: "left",
      valign: "top",
      placeholder: "Completition of SAITM workshop 2025",
    },
    {
      key: "assessment_name",
      x: 336,
      y: 826,
      width: 500,
      height: 60,
      font_family: "Montserrat-Medium.ttf",
      font_size: 49,
      color: "#000000",
      align: "left",
      valign: "top",
      placeholder: "SAITM workshop 2025",
    },
    {
      key: "course_description",
      x: 336,
      y: 904,
      width: 1200,
      height: 60,
      font_family: "Montserrat-Medium.ttf",
      font_size: 40,
      color: "#666666",
      align: "left",
      valign: "top",
      placeholder:
        "For completing and showing good performance and dedication throughout the program organized by Brudite at SAITM.",
    },
    {
      key: "verification_url",
      x: 1650,
      y: 1440,
      width: 600,
      height: 100,
      font_family: "Montserrat-Medium.ttf",
      font_size: 52,
      color: "#0066CC",
      align: "right",
      valign: "top",
      placeholder: "https://skillbrew.com/verify/FRTYI2B8J",
    },
  ]);

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    description: "",
    earning_criteria: "",
    organization: "Skillbrew.ai",
    summary: "",
    is_active: true,
    is_expire: false,
    auto_renewal: false,
    allow_revocation: true,
    expiry_duration: "",
  });

  useEffect(() => {
    if (!open) {
      resetS3State();
      setTemplateFile(null);
      setImagePreview("");
    }
  }, [open, resetS3State]);

  useEffect(() => {
    if (uploadSuccess && s3Path) {
      createCertificate(s3Path);
    }
  }, [uploadSuccess, s3Path]);

  useEffect(() => {
    if (uploadError && templateFile) {
      setLoading(false);
    }
  }, [uploadError, templateFile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files && e?.target?.files[0]) {
      const file = e?.target?.files[0];
      setTemplateFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleAddField = () => {
    setConfigFields([...configFields, { ...DEFAULT_FIELD }]);
  };

  const handleRemoveField = (index: number) => {
    setConfigFields(configFields?.filter((_, i) => i !== index));
  };

  const handleFieldChange = (
    index: number,
    field: keyof ConfigField,
    value: any
  ) => {
    const updatedFields = [...configFields];
    updatedFields[index] = {
      ...updatedFields[index],
      [field]: value,
    };
    setConfigFields(updatedFields);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (templateFile) {
        uploadFile(
          ApiConfig?.BadgeUploadPresigned,
          templateFile,
          templateFile?.type
        );
      } else {
        createCertificate("");
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const createCertificate = async (backgroundUrl: string) => {
    try {
      const certificateData: any = {
        code: formData?.code,
        name: formData?.name,
        description: formData?.description,
        earning_criteria: formData?.earning_criteria,
        background_url: backgroundUrl,
        config: {
          fields: configFields,
        },
        summary: formData?.summary,
        organization: formData?.organization,
        is_expire: formData?.is_expire,
        is_active: formData?.is_active,
        auto_renewal: formData?.auto_renewal,
        allow_revocation: formData?.allow_revocation,
      };

      if (formData?.is_expire && formData?.expiry_duration) {
        certificateData.expiry_duration = Number(formData?.expiry_duration);
      }

      const result = await dispatch(CreateCertificateAction(certificateData));
      if (result && result.status === true) {
        resetForm();
        onClose();
        if (onSuccess) {
          onSuccess();
        }
        const param ='?page=1&page_size=10';
        dispatch(CertificateListAction(param));
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      description: "",
      earning_criteria: "",
      organization: "Skillbrew.ai",
      summary: "",
      is_active: true,
      is_expire: false,
      auto_renewal: false,
      allow_revocation: true,
      expiry_duration: "",
    });
    setConfigFields([
      {
        key: "issued_date",
        x: 336,
        y: 554,
        width: 300,
        height: 50,
        font_family: "Montserrat-Medium.ttf",
        font_size: 31,
        color: "#333333",
        align: "left",
        valign: "top",
        placeholder: "Nov 14, 2024",
      },
      {
        key: "name",
        x: 336,
        y: 617,
        width: 800,
        height: 76,
        font_family: "Montserrat-Medium.ttf",
        font_size: 62,
        color: "#000000",
        align: "left",
        valign: "top",
        placeholder: "Participant Name",
      },
      {
        key: "awarded_for",
        x: 336,
        y: 720,
        width: 500,
        height: 50,
        font_family: "Montserrat-Medium.ttf",
        font_size: 31,
        color: "#666666",
        align: "left",
        valign: "top",
        placeholder: "Completition of SAITM workshop 2025",
      },
      {
        key: "assessment_name",
        x: 336,
        y: 826,
        width: 500,
        height: 60,
        font_family: "Montserrat-Medium.ttf",
        font_size: 49,
        color: "#000000",
        align: "left",
        valign: "top",
        placeholder: "SAITM workshop 2025",
      },
      {
        key: "course_description",
        x: 336,
        y: 904,
        width: 1200,
        height: 60,
        font_family: "Montserrat-Medium.ttf",
        font_size: 40,
        color: "#666666",
        align: "left",
        valign: "top",
        placeholder:
          "For completing and showing good performance and dedication throughout the program organized by Brudite at SAITM.",
      },
      {
        key: "verification_url",
        x: 1650,
        y: 1440,
        width: 600,
        height: 100,
        font_family: "Montserrat-Medium.ttf",
        font_size: 52,
        color: "#0066CC",
        align: "right",
        valign: "top",
        placeholder: "https://skillbrew.com/verify/FRTYI2B8J",
      },
    ]);
    setTemplateFile(null);
    setImagePreview("");
    setLoading(false);
    resetS3State();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Create Certificate</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: "#0555c8" }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="code"
                  label="Certificate Code"
                  value={formData?.code}
                  onChange={handleChange}
                  fullWidth
                  required
                  placeholder="e.g., CERT_2025_COMPLETION"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="name"
                  label="Certificate Name"
                  value={formData?.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  placeholder="e.g., Course Completion Certificate"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Description"
                  value={formData?.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Describe this certificate"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="earning_criteria"
                  label="Earning Criteria"
                  value={formData?.earning_criteria}
                  onChange={handleChange}
                  fullWidth
                  placeholder="How to earn this certificate"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="organization"
                  label="Organization"
                  value={formData?.organization}
                  onChange={handleChange}
                  fullWidth
                  placeholder="Issuing organization"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="summary"
                  label="Summary"
                  value={formData?.summary}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Brief summary"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Template Upload */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: "#0555c8" }}>
              Certificate Template
            </Typography>
            {imagePreview && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  src={imagePreview}
                  alt="Template preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 300,
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ width: "100%" }}
            />
            {templateFile && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", color: "#0555c8" }}
              >
                Selected: {templateFile?.name}
              </Typography>
            )}
            {uploadError && templateFile && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", color: "error.main" }}
              >
                Upload Error: {uploadError}
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Config Fields */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: "#0555c8" }}>
                Certificate Fields Configuration
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddField}
                variant="outlined"
                size="small"
              >
                Add Field
              </Button>
            </Box>

            {configFields?.map((field, index) => (
              <Paper
                key={index}
                sx={{ p: 2, mb: 2, backgroundColor: "#f9f9f9" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Field {index + 1}
                  </Typography>
                  <IconButton
                    onClick={() => handleRemoveField(index)}
                    color="error"
                    size="small"
                    disabled={configFields?.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Field Key"
                      value={field?.key}
                      onChange={(e) =>
                        handleFieldChange(index, "key", e?.target?.value)
                      }
                      fullWidth
                      size="small"
                      placeholder="e.g., issued_date, name"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Placeholder"
                      value={field?.placeholder}
                      onChange={(e) =>
                        handleFieldChange(
                          index,
                          "placeholder",
                          e?.target?.value
                        )
                      }
                      fullWidth
                      size="small"
                      placeholder="e.g., Nov 14, 2024"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="X Position"
                      type="number"
                      value={field?.x}
                      onChange={(e) =>
                        handleFieldChange(index, "x", Number(e.target.value))
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="Y Position"
                      type="number"
                      value={field?.y}
                      onChange={(e) =>
                        handleFieldChange(index, "y", Number(e.target.value))
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="Width"
                      type="number"
                      value={field?.width}
                      onChange={(e) =>
                        handleFieldChange(
                          index,
                          "width",
                          Number(e.target.value)
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="Height"
                      type="number"
                      value={field?.height}
                      onChange={(e) =>
                        handleFieldChange(
                          index,
                          "height",
                          Number(e.target.value)
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="Font Size"
                      type="number"
                      value={field?.font_size}
                      onChange={(e) =>
                        handleFieldChange(
                          index,
                          "font_size",
                          Number(e.target.value)
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="Color"
                      type="color"
                      value={field?.color}
                      onChange={(e) =>
                        handleFieldChange(index, "color", e.target.value)
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Horizontal Align</InputLabel>
                      <Select
                        value={field?.align}
                        onChange={(e) =>
                          handleFieldChange(index, "align", e.target.value)
                        }
                        label="Horizontal Align"
                      >
                        <MenuItem value="left">Left</MenuItem>
                        <MenuItem value="center">Center</MenuItem>
                        <MenuItem value="right">Right</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Vertical Align</InputLabel>
                      <Select
                        value={field?.valign}
                        onChange={(e) =>
                          handleFieldChange(index, "valign", e.target.value)
                        }
                        label="Vertical Align"
                      >
                        <MenuItem value="top">Top</MenuItem>
                        <MenuItem value="middle">Middle</MenuItem>
                        <MenuItem value="bottom">Bottom</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Font Family"
                      value={field?.font_family}
                      onChange={(e) =>
                        handleFieldChange(index, "font_family", e.target.value)
                      }
                      fullWidth
                      size="small"
                      placeholder="e.g., Montserrat-Medium.ttf"
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>

          <Divider />

          {/* Settings */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: "#0555c8" }}>
              Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_active"
                      checked={formData?.is_active}
                      onChange={handleChange}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_expire"
                      checked={formData?.is_expire}
                      onChange={handleChange}
                    />
                  }
                  label="Has Expiry"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      name="auto_renewal"
                      checked={formData?.auto_renewal}
                      onChange={handleChange}
                    />
                  }
                  label="Auto Renewal"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      name="allow_revocation"
                      checked={formData?.allow_revocation}
                      onChange={handleChange}
                    />
                  }
                  label="Allow Revocation"
                />
              </Grid>
              {formData?.is_expire && (
                <Grid item xs={12} md={6}>
                  <TextField
                    name="expiry_duration"
                    label="Expiry Duration (days)"
                    type="number"
                    value={formData?.expiry_duration}
                    onChange={handleChange}
                    fullWidth
                    helperText="Number of days until certificate expires"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || s3Loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || s3Loading || !formData?.code || !formData?.name}
          sx={{
            backgroundColor: "#0555c8",
            "&:hover": {
              backgroundColor: "#043c99",
            },
          }}
        >
          {loading || s3Loading ? (
            <CircularProgress size={24} />
          ) : (
            "Create Certificate"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCertificateModal;
